import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $, api } from '../lib/api';
import { Modal, Btn, Avatar } from './ui';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: (customer: any) => void;
}

interface FormData {
  nickname: string;
  source: string;
  wechatId: string;
  phone: string;
  assignedScreenshot: string;
  aiRecognizedText: string;
  assigneeId: number | null;
}

// batch-fix#H3：来源归一——存英文枚举 value、显中文 label(对齐 CUSTOMER_SOURCES + 截图分配/导入口径，存量中文由迁移 0077 清)。
const SOURCES: Array<{ value: string; label: string }> = [
  { value: 'xiaohongshu', label: '小红书' },
  { value: 'douyin', label: '抖音' },
  { value: 'wechat_channel', label: '视频号' },
  { value: 'baidu', label: '百度推广' },
  { value: 'website', label: '官网' },
  { value: 'phone', label: '电话' },
  { value: 'other', label: '其他' },
];
const sourceLabel = (v?: string) => SOURCES.find((s) => s.value === v)?.label ?? v ?? '—';


export function NewCustomerModal({ open, onClose, onCreated }: Props) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormData>({
    nickname: '',
    source: 'xiaohongshu',
    wechatId: '',
    phone: '',
    assignedScreenshot: '',
    aiRecognizedText: '',
    assigneeId: null,
  });
  const [submitting, setSubmitting] = useState(false);
  // v1.5 C-1.a #10：接真 OCR（复用智能分配 customer-screenshot 接口）
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [ocrConfidence, setOcrConfidence] = useState<number | null>(null);
  const qc = useQueryClient();

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep(1);
      setForm({
        nickname: '', source: 'xiaohongshu', wechatId: '', phone: '',
        assignedScreenshot: '', aiRecognizedText: '', assigneeId: null,
      });
      setPreviewUrl(null);
      setOcrConfidence(null);
    }
  }, [open]);

  // v1.5 C-1.a #10：upload + analyze，OCR 成功自动回填表单 + 进入 step 2
  const uploadAndAnalyze = useMutation({
    mutationFn: async (file: File | Blob) => {
      const fObj = file instanceof File ? file : new File([file], `paste-${Date.now()}.png`, { type: file.type });
      if (fObj.size > 8 * 1024 * 1024) throw new Error('图片超 8MB，请压缩');
      const fd = new FormData();
      fd.append('file', fObj);
      const upResp = await api.post('/customer-screenshot/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const up = upResp.data as { analysis_id: number; image_url: string };
      const a = await $.post<any>(`/customer-screenshot/${up.analysis_id}/analyze`, {});
      return { analysis: a, imageUrl: up.image_url };
    },
    onSuccess: ({ analysis, imageUrl }) => {
      const p = analysis.customer_profile ?? {};
      setForm((f) => ({
        ...f,
        nickname: p.xiaohongshu_name ?? p.nickname ?? f.nickname,
        source: SOURCES.some((s) => s.value === p.source) ? p.source : f.source, // 评审#H3：AI 预填来源仅取合法英文枚举值，否则保留默认(防中文/旧值被 DTO @IsIn 拒)
        wechatId: p.wechat_id ?? f.wechatId,
        phone: p.phone ?? f.phone,
        assignedScreenshot: imageUrl ?? f.assignedScreenshot,
        aiRecognizedText: analysis.ocr_text ?? f.aiRecognizedText,
      }));
      // 简单置信度估算：有几个字段被识别到
      const filled = ['xiaohongshu_name','source','wechat_id','phone'].filter((k) => p[k]).length;
      setOcrConfidence(Math.round((filled / 4) * 100));
      setStep(2);
    },
    onError: (e: any) => {
      // OCR 失败仍允许手动填（不阻塞流程）
      const msg = e?.response?.data?.message ?? e?.message ?? 'OCR 识别失败';
      message.warning(`${msg}（下一步可手动填）`);
      setStep(2);
    },
  });

  const handleImageFile = (f: File | Blob) => {
    const fObj = f instanceof File ? f : new File([f], `paste-${Date.now()}.png`, { type: f.type });
    if (fObj.size > 8 * 1024 * 1024) {
      message.error(`图片 ${(fObj.size / 1024 / 1024).toFixed(1)} MB 超 8MB 上限`);
      return;
    }
    if (previewUrl?.startsWith('blob:')) {
      try { URL.revokeObjectURL(previewUrl); } catch {}
    }
    setPreviewUrl(URL.createObjectURL(fObj));
    uploadAndAnalyze.mutate(fObj);
  };

  // 全局 paste 监听（modal 打开期间）
  useLayoutEffect(() => {
    if (!open) return;
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          const blob = items[i].getAsFile();
          if (blob) { handleImageFile(blob); e.preventDefault(); break; }
        }
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [open]);

  /** 巡检#7+8：拉全员（不再硬过滤 role=sales），候选含 sales + manager（主管也做业绩、可被分配）。错误非致命。 */
  const { data: users } = useQuery({
    queryKey: ['users-assignable'],
    queryFn: async () => {
      try {
        return await $.get<{ items: any[]; total: number }>('/users', { page: 1, pageSize: 200 });
      } catch { return { items: [], total: 0 }; }
    },
    enabled: open && step >= 3,
  });

  /** 按负载打分（manager 推荐权重同 sales，不再 -10）。candidates = 全量(sales+manager)，recommendations = Top3。 */
  const candidates = (users?.items ?? [])
    .filter((u) => u.isActive && ['sales', 'manager'].includes(u.role))
    .map((u) => {
      const capacity = u.customerCapacity ?? 5;
      const current = u.currentCustomerCount ?? 0;
      const load = capacity > 0 ? current / capacity : 1;
      const score = Math.max(40, Math.round(95 - load * 60));
      return { ...u, score, capacity, current };
    })
    .sort((a, b) => b.score - a.score);
  const recommendations = candidates.slice(0, 3);

  // Auto-pick top recommendation on entering step 3
  useEffect(() => {
    if (step === 3 && !form.assigneeId && recommendations.length > 0) {
      setForm((f) => ({ ...f, assigneeId: recommendations[0].id }));
    }
  }, [step, recommendations.length]);

  // Step 1 = upload (optional, always allow skip in mock mode)
  // Step 2 = AI 识别 form (need nickname)
  // Step 3 = assignee picker (need assigneeId)
  // Step 4 = confirm summary
  const canNext = step === 1 ? true
    : step === 2 ? !!form.nickname.trim()
    : step === 3 ? !!form.assigneeId
    : true;

  const selectedAssignee = candidates.find((u) => u.id === form.assigneeId);

  const submit = async () => {
    if (!form.nickname.trim()) {
      message.warning('请填写客户姓名');
      setStep(1);
      return;
    }
    setSubmitting(true);
    try {
      const created = await $.post('/customers', {
        nickname: form.nickname.trim(),
        source: form.source,
        wechatId: form.wechatId.trim() || undefined,
        phone: form.phone.trim() || undefined,
        assigneeId: form.assigneeId ?? undefined,
        assignedScreenshot: form.assignedScreenshot || undefined,
        aiRecognizedText: form.aiRecognizedText || undefined,
      });
      message.success(`「${form.nickname}」已分配${selectedAssignee ? `给 ${selectedAssignee.realName}` : ''} · 5 分钟内需接收`);
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['customers-summary'] });
      qc.invalidateQueries({ queryKey: ['sidebar-stage-counts'] });
      onCreated?.(created);
      onClose();
    } catch (e: any) {
      message.error(e?.response?.data?.message ?? e?.message ?? '创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="新增客户 · 智能分配"
      onClose={onClose}
      footer={
        <>
          <Btn kind="default" onClick={onClose}>取消</Btn>
          {step > 1 && <Btn kind="default" onClick={() => setStep((s) => s - 1)}>上一步</Btn>}
          {step < 4 ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={!canNext}
              style={!canNext ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
              onClick={() => canNext && setStep((s) => s + 1)}
            >
              {canNext ? '下一步' : '请先填写'}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              disabled={submitting}
              onClick={submit}
              style={submitting ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              {submitting ? '推送中…' : `确认推送${selectedAssignee ? `给 ${selectedAssignee.realName}` : ''}`}
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
            </button>
          )}
        </>
      }
    >
      <StepBar current={step} />
      {step === 1 && <Step1Upload onFile={handleImageFile} loading={uploadAndAnalyze.isPending} previewUrl={previewUrl} />}
      {step === 2 && <Step2AiRecognize form={form} setForm={setForm} confidence={ocrConfidence} />}
      {step === 3 && <Step3Assign form={form} setForm={setForm} recs={recommendations} allCandidates={candidates} />}
      {step === 4 && <Step4Confirm form={form} assignee={selectedAssignee} />}
    </Modal>
  );
}

function StepBar({ current }: { current: number }) {
  const steps = ['上传截图', 'AI 识别', '分配人选', '确认推送'];
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      padding: '18px 24px', borderBottom: '1px solid var(--border-light)',
      background: '#fafbfc',
      margin: '-24px -24px 18px -24px',
    }}>
      {steps.map((label, i) => {
        const n = i + 1;
        const state = n < current ? 'done' : n === current ? 'active' : 'pending';
        return (
          <span key={label} style={{ display: 'flex', alignItems: 'center', flex: i < steps.length - 1 ? 1 : undefined }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div className="stage-num" style={{
                width: 22, height: 22, borderRadius: 11,
                ...(state === 'done' ? { background: 'var(--success)', borderColor: 'var(--success)', color: 'white' }
                  : state === 'active' ? { background: 'var(--primary)', borderColor: 'var(--primary)', color: 'white', boxShadow: '0 0 0 3px var(--primary-bg)' }
                  : {}),
              }}>
                {state === 'done'
                  ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 13l4 4L19 7" /></svg>
                  : n}
              </div>
              <span style={{
                fontSize: 12.5,
                color: state === 'pending' ? 'var(--text-3)' : state === 'active' ? 'var(--primary)' : 'var(--text-1)',
                fontWeight: state === 'active' ? 500 : 400,
              }}>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div style={{
                flex: 1, height: 1.5, margin: '0 10px',
                background: n < current ? 'var(--success)' : 'var(--border)',
              }} />
            )}
          </span>
        );
      })}
    </div>
  );
}

function Step1Upload({
  onFile, loading, previewUrl,
}: { onFile: (f: File) => void; loading: boolean; previewUrl: string | null }) {
  return (
    <>
      <AiResult
        title="上传客户截图（可选）"
        text={'从小红书/抖音/微信把客户私信截图拖进来或 Ctrl+V 粘贴。AI 识别昵称/来源/微信号/手机号等。识别失败可下一步手动填。'}
      />
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files?.[0];
          if (f && f.type.startsWith('image/')) onFile(f);
        }}
        style={{
          border: '2px dashed var(--border)', borderRadius: 10,
          padding: previewUrl ? 16 : 48, textAlign: 'center', color: 'var(--text-3)',
          background: '#fafbfc',
        }}>
        {previewUrl ? (
          <>
            <img src={previewUrl} alt="预览" style={{ maxHeight: 220, maxWidth: '100%', borderRadius: 6 }} />
            <div style={{ marginTop: 10, fontSize: 13, color: loading ? 'var(--primary)' : 'var(--success-text)' }}>
              {loading ? '🤖 AI 识别中（5-10 秒）…' : '✓ 已上传，进入识别结果'}
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
            <div style={{ fontSize: 13 }}>拖拽 / Ctrl+V 粘贴截图，或</div>
            <label style={{ marginTop: 8, display: 'inline-block', color: 'var(--primary)', cursor: 'pointer' }}>
              选择本地文件
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onFile(f);
              }} />
            </label>
          </>
        )}
      </div>
      <div style={{ marginTop: 16, fontSize: 12.5, color: 'var(--text-3)' }}>
        💡 上传后会调阿里云 OCR + AI 识别，自动填到下一步表单（可编辑）。跳过仍可手动创建。
      </div>
    </>
  );
}

function Step2AiRecognize({ form, setForm, confidence }: { form: FormData; setForm: (f: FormData) => void; confidence: number | null }) {
  return (
    <>
      <AiResult
        title="AI 识别结果（可编辑）"
        text={confidence != null
          ? '系统已从截图中识别以下字段，确认/补充后进入「分配人选」。'
          : '没识别到截图（或跳过了上传），请手动填写后进入「分配人选」。'}
        badge={confidence != null ? `识别字段 ${confidence}%` : '手动填写'}
      />
      <div className="form-grid">
        <div className="form-field">
          <label className="form-label required">客户姓名</label>
          <input
            className="form-input"
            value={form.nickname}
            onChange={(e) => setForm({ ...form, nickname: e.target.value })}
            placeholder="例：张静"
            autoFocus
          />
          <div className="form-hint">AI 自动识别 · 可修改</div>
        </div>
        <div className="form-field">
          <label className="form-label required">客户来源</label>
          <select className="form-input" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
            {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <div className="form-hint">影响 AI 智能推荐分配</div>
        </div>
        <div className="form-field">
          <label className="form-label">微信号</label>
          <input className="form-input" value={form.wechatId} onChange={(e) => setForm({ ...form, wechatId: e.target.value })} placeholder="选填" />
        </div>
        <div className="form-field">
          <label className="form-label">手机号</label>
          <input className="form-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="选填" />
        </div>
      </div>
    </>
  );
}

function Step3Assign({
  form, setForm, recs, allCandidates,
}: {
  form: FormData;
  setForm: (f: FormData) => void;
  recs: any[];
  allCandidates: any[];
}) {
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState('');
  const recIds = new Set(recs.map((u) => Number(u.id)));
  const others = allCandidates
    .filter((u) => !recIds.has(Number(u.id)))
    .filter((u) => !query.trim() || (u.realName ?? '').includes(query) || (u.username ?? '').includes(query));
  return (
    <>
      <AiResult
        title="智能推荐分配"
        text={'根据历史转化率、当前负载、健康度均值，给你打分。点选一张卡片，或按「上一步」修改客户信息。'}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {recs.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
            暂无可分配的销售员。请先在团队页面添加成员。
          </div>
        )}
        {recs.map((u, i) => {
          const selected = form.assigneeId === u.id;
          return (
            <div
              key={u.id}
              onClick={() => setForm({ ...form, assigneeId: u.id })}
              style={{
                position: 'relative',
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 14px',
                border: '1px solid ' + (selected ? 'var(--primary)' : 'var(--border)'),
                borderRadius: 8,
                background: selected ? 'var(--primary-bg)' : 'var(--surface)',
                boxShadow: selected ? '0 0 0 3px rgba(51, 112, 255, 0.1)' : undefined,
                cursor: 'pointer',
              }}
            >
              {i === 0 && (
                <div style={{
                  position: 'absolute', top: -7, left: -7,
                  width: 22, height: 22, borderRadius: 11,
                  background: 'linear-gradient(135deg, #ffba36, #ff9500)',
                  color: 'white', fontSize: 11, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(255, 149, 0, 0.4)',
                }}>{i + 1}</div>
              )}
              <Avatar userId={u.id} src={u.avatarUrl} name={u.realName ?? u.username} size="md" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-1)' }}>{u.realName ?? u.username}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
                  {roleLabel(u.role)} · 当前 {u.current}/{u.capacity} 客户
                </div>
                <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                  <span className="rec-tag good" style={{ fontSize: 11, padding: '1px 6px', borderRadius: 3, background: 'var(--success-bg)', color: 'var(--success-text)' }}>负载 {Math.round((u.current / Math.max(1, u.capacity)) * 100)}%</span>
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{
                  fontSize: 22, fontWeight: 600,
                  color: selected ? 'var(--primary)' : 'var(--text-1)',
                  fontVariantNumeric: 'tabular-nums', lineHeight: 1,
                }}>{u.score}</div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>推荐分</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 巡检#7：手动选择其他员工（含主管），不止 Top3 */}
      <div style={{ marginTop: 12 }}>
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: 12.5, padding: 0 }}>
          {showAll ? '收起手动选择' : `▸ 手动选择其他员工（${allCandidates.length} 人，含主管）`}
        </button>
        {showAll && (
          <div style={{ marginTop: 8 }}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索姓名 / 账号"
              style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, marginBottom: 8 }} />
            <div style={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {others.length === 0 && (
                <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-3)', fontSize: 12 }}>无匹配员工</div>
              )}
              {others.map((u) => {
                const selected = form.assigneeId === u.id;
                return (
                  <div key={u.id} onClick={() => setForm({ ...form, assigneeId: u.id })}
                       style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer',
                                border: '1px solid ' + (selected ? 'var(--primary)' : 'var(--border)'), borderRadius: 6,
                                background: selected ? 'var(--primary-bg)' : 'var(--surface)' }}>
                    <Avatar userId={u.id} src={u.avatarUrl} name={u.realName ?? u.username} size="sm" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{u.realName ?? u.username}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{roleLabel(u.role)} · 当前 {u.current}/{u.capacity} 客户</div>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: selected ? 'var(--primary)' : 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>{u.score}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div style={{
        marginTop: 16,
        background: 'var(--warning-bg)', border: '1px solid #ffd591', borderRadius: 8,
        padding: '10px 14px', fontSize: 12.5, color: 'var(--warning-text)',
        display: 'flex', gap: 8, alignItems: 'flex-start', lineHeight: 1.5,
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
        <div><strong>确认推送后</strong>，客户会通过钉钉+APP+短信三通道推送，<strong style={{ color: 'var(--danger-text)' }}>5 分钟内必须点击接收</strong>，超时自动回流公海。</div>
      </div>
    </>
  );
}

function roleLabel(role: string): string {
  return ({ sales: '见习销售', manager: '主管', director: '总监', admin: '管理员', academic: '教务', academic_director: '教务总监' } as Record<string, string>)[role] ?? role;
}

function Step4Confirm({ form, assignee }: { form: FormData; assignee: any }) {
  return (
    <>
      <AiResult title="确认信息" text={'点击「确认推送」后，客户进入 L1 触达试用期，分配人员将收到三通道通知。'} badge="即将提交" />
      <div className="detail-kv" style={{ gridTemplateColumns: '90px 1fr', rowGap: 12, fontSize: 14 }}>
        <span className="detail-kv-label">客户姓名</span><span className="detail-kv-value">{form.nickname || '—'}</span>
        <span className="detail-kv-label">来源</span><span className="detail-kv-value">{sourceLabel(form.source)}</span>
        <span className="detail-kv-label">微信号</span><span className="detail-kv-value">{form.wechatId || '—'}</span>
        <span className="detail-kv-label">手机号</span><span className="detail-kv-value">{form.phone || '—'}</span>
        <span className="detail-kv-label">分配给</span>
        <span className="detail-kv-value" style={{ color: 'var(--primary)', fontWeight: 500 }}>
          {assignee ? `${assignee.realName ?? assignee.username} · 推荐分 ${assignee.score}` : '—'}
        </span>
        <span className="detail-kv-label">初始阶段</span><span className="detail-kv-value">L1 触达 · 试用期</span>
      </div>
    </>
  );
}

function AiResult({ title, text, badge }: { title: string; text: string; badge?: string }) {
  return (
    <div style={{
      background: 'var(--primary-bg)', border: '1px solid var(--primary-border)', borderRadius: 8,
      padding: '14px 16px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2">
          <path d="M12 2L9 9l-7 1 5 5-1 7 6-3 6 3-1-7 5-5-7-1z" />
        </svg>
        <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--primary)' }}>{title}</span>
        {badge && (
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--success-text)', background: 'var(--success-bg)', padding: '1px 7px', borderRadius: 8 }}>{badge}</span>
        )}
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>{text}</div>
    </div>
  );
}
